import { useEffect, useState } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";

// Non-production only. In production these overrides are forced off regardless
// of any persisted value, and the Dev Panel that sets them never renders.
const IS_NON_PROD = process.env.NEXT_PUBLIC_ENV !== "production";

const DEFAULT_OVERRIDES = {
  /** Force the BudgetWise "Settlement Bill" view on any account. */
  simulateSettlement: false,
  /**
   * Replace the cumulative used-vs-paid difference (dollars) on the current
   * BudgetWise period. Negative settles into a credit, positive into a debit.
   * `null` leaves the account's real figure alone.
   */
  usageDifference: null as number | null,
};

export type DevOverrides = typeof DEFAULT_OVERRIDES;
export type DevOverrideKey = keyof DevOverrides;

/** The subset of overrides the Dev Panel can render as a checkbox. */
export type BooleanOverrideKey = {
  [K in DevOverrideKey]: DevOverrides[K] extends boolean ? K : never;
}[DevOverrideKey];

interface DevOverridesStore extends DevOverrides {
  setOverride: <K extends DevOverrideKey>(
    key: K,
    value: DevOverrides[K],
  ) => void;
  reset: () => void;
}

/**
 * Runtime QA overrides, written by the Dev Panel and read by feature code via
 * `useDevOverride`. Persisted to localStorage so toggles survive a reload.
 */
export const useDevOverrides = create<DevOverridesStore>()(
  persist(
    (set) => ({
      ...DEFAULT_OVERRIDES,
      setOverride: (key, value) =>
        set({ [key]: value } as Partial<DevOverrides>),
      reset: () => set(DEFAULT_OVERRIDES),
    }),
    { name: "billwise-dev-overrides" },
  ),
);

/**
 * Read a single override from feature code. Safe for SSR/hydration (returns the
 * default until the client has mounted, so server and first client render
 * match) and always returns the default in production, so a stale persisted
 * value can never affect prod behavior.
 */
export function useDevOverride<K extends DevOverrideKey>(
  key: K,
): DevOverrides[K] {
  const value = useDevOverrides((state) => state[key]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => setHydrated(true), []);

  if (!IS_NON_PROD) return DEFAULT_OVERRIDES[key];
  return hydrated ? value : DEFAULT_OVERRIDES[key];
}
